import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { PencilSimple } from "@phosphor-icons/react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import WizardShell from "./wizard/WizardShell";
import ChipGroup, { Q } from "./wizard/ChipGroup";
import PhotoPicker from "./PhotoPicker";
import { SeverityDots } from "./SeverityMark";
import UserSelect from "./wizard/UserSelect";
import PlantGrid from "./wizard/PlantGrid";
import PlantSwitch from "./wizard/PlantSwitch";

export { PlantGrid };
import { useAuth } from "../context/AuthContext";
import { listPlants, createEvent, listUsers, createTask, updatePlant } from "../lib/api";
import { SEVERITY_META, formatDate } from "../lib/theme";
import { useSiteOptions, mergeOptions } from "../lib/options";
import { ISSUE_OPTIONS, SYMPTOMS, CAUSES, AFFECTED, ACTIONS, TREATMENTS, plusDays, joinText } from "../lib/intakeOptions";
import DateChips from "./wizard/DateChips";
import { useNavigate } from "react-router-dom";

export const CATEGORIES = ISSUE_OPTIONS.map(({ value, label }) => ({ value, label }));

export const CATEGORY_LABELS = {
  ...Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label])),
  fungal: "Fungal / mold",
  environmental: "Environmental stress",
};

export const SPREAD_LABELS = { isolated: "Isolated", spreading: "Spreading", widespread: "Widespread" };

const emptyForm = () => ({
  plant_id: "", severity: "p3", category: "", symptom_tags: [], symptoms: "", cause_tags: [], suspected_cause: "",
  affected_parts: [], spread: "isolated", action_tags: [], action_taken: "", quarantine: false, treatment_tags: [], treatment_plan: "",
  products_used: "", follow_choice: "7", follow_up_date: plusDays(7), photo_paths: [], assignee_id: "",
});

function SeverityPicker({ value, onChange }) {
  const keys = Object.keys(SEVERITY_META);
  const idx = keys.indexOf(value);
  const sel = SEVERITY_META[value];
  return (
    <div data-testid="critical-severity-group">
      <div className="sm:hidden space-y-2" role="radiogroup" aria-label="Priority">
        {keys.map((k) => {
          const s = SEVERITY_META[k];
          const on = k === value;
          return (
            <button key={k} type="button" role="radio" aria-checked={on} data-testid={`critical-severity-m-${k}`} onClick={() => onChange(k)}
              className={`w-full min-h-[52px] rounded-2xl px-4 py-2.5 flex items-center gap-3 text-left transition-[background-color,box-shadow] duration-200 ${on ? "ring-2" : "bg-muted/60"}`}
              style={on ? { background: `${s.color}14`, boxShadow: `inset 0 0 0 2px ${s.color}` } : undefined}>
              <SeverityDots level={s.level} color={s.color} />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold" style={on ? { color: s.color } : undefined}>P{s.level} · {s.label}</span>
                <span className="block text-xs text-muted-foreground leading-snug break-words">{s.hint}</span>
              </span>
            </button>
          );
        })}
      </div>
      <div className="max-sm:hidden">
      <div className="relative grid grid-cols-5 p-1 rounded-[14px] bg-muted/70" role="radiogroup">
        <span aria-hidden className="absolute top-1 bottom-1 left-1 rounded-[10px] transition-[transform,background-color] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]"
          style={{ width: "calc((100% - 0.5rem) / 5)", transform: `translateX(${idx * 100}%)`, background: sel.color, boxShadow: `0 6px 14px -6px ${sel.color}` }} />
        {keys.map((k) => {
          const s = SEVERITY_META[k];
          const on = k === value;
          return (
            <button key={k} type="button" role="radio" aria-checked={on} data-testid={`critical-severity-${k}`} onClick={() => onChange(k)}
              className={`relative z-10 h-10 rounded-[10px] text-[13px] max-sm:text-[11px] whitespace-nowrap inline-flex items-center justify-center gap-1.5 transition-colors duration-200 ${on ? "font-semibold text-white" : "text-muted-foreground hover:text-foreground"}`}>
              <SeverityDots level={s.level} color={on ? "#fff" : s.color} text={on ? s.color : "#fff"} /><span className="max-sm:hidden">{s.label}</span>
            </button>
          );
        })}
      </div>
      <div className="mt-3 rounded-2xl px-4 py-3 transition-colors" style={{ background: `${sel.color}12` }} data-testid="critical-severity-label">
        <p className="text-sm font-semibold" style={{ color: sel.color }}>Priority {sel.level} · {sel.label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{sel.hint}</p>
      </div>
      </div>
    </div>
  );
}

function Review({ form, plant, users, me, goTo }) {
  const sev = SEVERITY_META[form.severity];
  const owner = form.assignee_id ? (form.assignee_id === me?.id ? "You" : users.find((u) => u.id === form.assignee_id)?.name) : "No one yet";
  const rows = [
    ["plant", "Plant", plant ? `${plant.nickname} · ${plant.plant_code}` : "—"],
    ["issue", "Issue", `${CATEGORY_LABELS[form.category] || "—"} · ${SPREAD_LABELS[form.spread]}`],
    ["issue", "Symptoms", joinText(form.symptom_tags, form.symptoms) || "—"],
    ["issue", "Affected", form.affected_parts.join(", ") || "—"],
    ["severity", "Priority", <span key="s" style={{ color: sev.color }} className="font-semibold">P{sev.level} · {sev.label}</span>],
    ["severity", "Likely cause", joinText(form.cause_tags, form.suspected_cause) || "—"],
    ["response", "Done so far", joinText(form.action_tags, form.action_taken) || "—"],
    ["response", "Plan", joinText(form.treatment_tags, form.treatment_plan) || "—"],
    ["response", "Follow-up", form.follow_up_date ? formatDate(form.follow_up_date + "T12:00:00") : "None"],
    ["response", "Owner", owner],
    ["response", "Quarantine", form.quarantine ? "Yes" : "No"],
  ];
  return (
    <dl className="rounded-2xl bg-muted/40 divide-y divide-border/60" data-testid="critical-review">
      {rows.map(([step, k, v], i) => (
        <div key={i} className="flex items-start gap-4 px-4 py-3">
          <dt className="w-24 max-sm:w-[72px] shrink-0 text-xs text-muted-foreground pt-0.5">{k}</dt>
          <dd className="flex-1 min-w-0 text-sm">{v}</dd>
          <button type="button" onClick={() => goTo(step)} aria-label={`Edit ${k}`} data-testid={`review-edit-${k.toLowerCase().replace(/\s+/g, "-")}`} className="-m-2.5 p-2.5 shrink-0 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"><PencilSimple className="h-3.5 w-3.5" /></button>
        </div>
      ))}
      {form.photo_paths.length > 0 && <p className="px-4 py-3 text-xs text-muted-foreground">{form.photo_paths.length} photo{form.photo_paths.length > 1 ? "s" : ""} attached</p>}
    </dl>
  );
}

export default function CriticalEventDialog({ open, onOpenChange, defaultPlantId = null, onCreated }) {
  const siteOpts = useSiteOptions();
  const issueOptions = mergeOptions(ISSUE_OPTIONS.map((o) => ({ ...o, key: o.value })), siteOpts.acute_categories).map((o) => ({ ...o, value: o.key }));
  const { user } = useAuth();
  const [plants, setPlants] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);
  const navigate = useNavigate();
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  useEffect(() => {
    if (!open) return;
    listPlants().then((ps) => setPlants(ps.filter((p) => p.status !== "deceased" || p.id === defaultPlantId))).catch(() => {});
    listUsers().then(setUsers).catch(() => {});
    setForm({ ...emptyForm(), plant_id: defaultPlantId || "" });
    setStep(0);
    setDone(null);
  }, [open, defaultPlantId]);

  const steps = useMemo(() => [
    !defaultPlantId && { key: "plant", title: "Which plant?", subtitle: "Tap the plant that needs urgent care.", hint: "Plants with a red dot already have an open case." },
    { key: "issue", title: "What's wrong?", subtitle: "Pick the issue, then tap everything you can see.", hint: "Pick the closest match. A close-up photo helps the team see it too." },
    { key: "severity", title: "How serious is it?", subtitle: "Priority 1 is the most serious, 5 the least. It sets who gets alerted and how fast.", hint: "Not sure? Start at 3. You can raise or lower it any time from the case." },
    { key: "response", title: "What's the plan?", subtitle: "Record what's been done and what happens next.", optional: true, hint: "In a hurry? Skip this and add the plan later in the case thread." },
    { key: "review", title: "Review and log", subtitle: "Check the record. Tap the pencil to change anything." },
  ].filter(Boolean), [defaultPlantId]);
  const cur = steps[step].key;
  const goTo = (key) => setStep(steps.findIndex((s) => s.key === key));
  const plant = plants.find((p) => p.id === form.plant_id);
  const sev = SEVERITY_META[form.severity];

  const pickCategory = (c) => setForm((f) => ({
    ...f, category: c,
    symptom_tags: f.symptom_tags.filter((t) => (SYMPTOMS[c] || []).includes(t)),
    cause_tags: f.cause_tags.filter((t) => (CAUSES[c] || []).includes(t)),
    treatment_tags: f.treatment_tags.filter((t) => (TREATMENTS[c] || []).includes(t)),
  }));

  const canNext = cur === "plant" ? !!form.plant_id
    : cur === "issue" ? !!form.category && (form.symptom_tags.length > 0 || !!form.symptoms.trim())
      : true;

  const submit = async () => {
    try {
      setSaving(true);
      const details = {
        category: form.category, symptoms: joinText(form.symptom_tags, form.symptoms), symptom_tags: form.symptom_tags,
        suspected_cause: joinText(form.cause_tags, form.suspected_cause), cause_tags: form.cause_tags,
        affected_parts: form.affected_parts, spread: form.spread,
        action_taken: joinText(form.action_tags, form.action_taken), action_tags: form.action_tags, quarantine: form.quarantine,
        treatment_plan: joinText(form.treatment_tags, form.treatment_plan), treatment_tags: form.treatment_tags,
        products_used: form.products_used.trim() || null, follow_up_date: form.follow_up_date || null, additional_notes: null,
      };
      const ev = await createEvent(form.plant_id, { event_type: "critical", severity: form.severity, notes: details.symptoms, photo_paths: form.photo_paths, details });
      if (form.quarantine) await updatePlant(form.plant_id, { status: "quarantine" }).catch(() => toast.error("Could not move plant to quarantine"));
      if (form.assignee_id) {
        await createTask({
          plant_id: form.plant_id, title: `Acute follow-up: ${CATEGORY_LABELS[form.category]}`, notes: details.treatment_plan,
          assignee_id: form.assignee_id, due_date: details.follow_up_date, priority: "critical", critical_event_id: ev.id,
        }).catch(() => toast.error("Event saved, but the follow-up task could not be created"));
      }
      onCreated?.(ev);
      window.dispatchEvent(new Event("acute:changed"));
      const who = form.assignee_id ? (form.assignee_id === user?.id ? "You have" : `${users.find((u) => u.id === form.assignee_id)?.name?.split(" ")[0] || "They"} has`) : null;
      setDone({ art: "ambulance", title: "Acute event logged", sub: `${plant?.nickname || "The plant"} is flagged for the team.${who ? ` ${who} a follow-up task${details.follow_up_date ? ` for ${formatDate(details.follow_up_date + "T12:00:00")}` : ""}.` : ""}`,
        actions: [
          { label: "Choose an order set", onClick: () => { onOpenChange(false); navigate(`/cases/${ev.id}?orders=1`); } },
          { label: "Open the case", onClick: () => { onOpenChange(false); navigate(`/cases/${ev.id}`); } },
        ] });
    } catch {
      toast.error("Could not save acute event");
    } finally {
      setSaving(false);
    }
  };

  const others = users.filter((u) => u.id !== user?.id);
  const other = others.find((u) => u.id === form.assignee_id);
  const ownerOptions = [["", "No one yet"], ...(user ? [[user.id, "Me"]] : []), ...(other ? [[other.id, other.name]] : [])];

  return (
    <WizardShell open={open} onOpenChange={onOpenChange} testid="critical-event-dialog" kicker={plant && cur !== "plant" ? `Acute event · ${plant.nickname}` : "Acute event"}
      steps={steps} step={step} onBack={() => setStep((s) => s - 1)} onNext={() => setStep((s) => s + 1)} canNext={canNext}
      dirty={!!form.category || form.photo_paths.length > 0 || !!form.symptoms.trim()} discardText="This acute event hasn't been logged yet. Everything you've entered will be lost."
      draftKey={`acute:${defaultPlantId || "any"}`} draftValue={{ form, step }} onRestoreDraft={(d) => { setForm(d.form); setStep(d.step); }}
      done={done} doneColor={sev.color} onSkip={() => goTo("review")}
      isLast={cur === "review"} onFinish={submit} saving={saving} finishLabel="Log acute event" finishStyle={{ background: sev.color, color: "#fff" }}>
      {cur === "plant" && <PlantGrid plants={plants} value={form.plant_id} onPick={(id) => { set("plant_id")(id); setTimeout(() => setStep(1), 180); }} />}

      {cur === "issue" && (
        <>
          {defaultPlantId && <PlantSwitch testid="critical-plant-switch" plants={plants} value={form.plant_id} onChange={set("plant_id")} />}
          <Q label="Photo" hint="Add a close-up of the problem area.">
            <PhotoPicker value={form.photo_paths} onChange={set("photo_paths")} testid="critical-photos" />
          </Q>
          <Q label="Issue type"><ChipGroup large prefix="category" options={issueOptions} value={form.category} onChange={pickCategory} allowClear={false} /></Q>
          {form.category && (
            <>
              <Q label="What do you see?" hint="Tap all that apply. These become the symptom record.">
                <ChipGroup multi prefix="symptom" options={SYMPTOMS[form.category]} value={form.symptom_tags} onChange={set("symptom_tags")} />
                <Textarea data-testid="critical-symptoms-input" value={form.symptoms} onChange={set("symptoms")} placeholder="Anything else? (optional)" className="mt-3 rounded-xl min-h-[64px] bg-muted/40 border-0" />
              </Q>
              <Q label="Where on the plant?"><ChipGroup multi prefix="affected" options={AFFECTED} value={form.affected_parts} onChange={set("affected_parts")} /></Q>
              <Q label="How far has it spread?"><ChipGroup prefix="spread" options={Object.entries(SPREAD_LABELS)} value={form.spread} onChange={set("spread")} allowClear={false} /></Q>
            </>
          )}
        </>
      )}

      {cur === "severity" && (
        <>
          <Q label="Priority" hint="How much danger is the plant in right now?">
            <SeverityPicker value={form.severity} onChange={set("severity")} />
          </Q>
          <Q label="Likely cause" hint="Optional. Tap any that might be behind it.">
            <ChipGroup multi prefix="cause" options={CAUSES[form.category] || CAUSES.other} value={form.cause_tags} onChange={set("cause_tags")} />
            <Input data-testid="critical-cause-input" value={form.suspected_cause} onChange={set("suspected_cause")} placeholder="Something else? (optional)" className="mt-3 rounded-xl h-11 bg-muted/40 border-0" />
          </Q>
        </>
      )}

      {cur === "response" && (
        <>
          <Q label="Done so far"><ChipGroup multi prefix="action" options={ACTIONS} value={form.action_tags} onChange={set("action_tags")} /></Q>
          <Q label="Treatment plan">
            <ChipGroup multi prefix="treatment" options={TREATMENTS[form.category] || TREATMENTS.other} value={form.treatment_tags} onChange={set("treatment_tags")} />
            <Input data-testid="critical-products-input" value={form.products_used} onChange={set("products_used")} placeholder="Product & dosage (optional)" className="mt-3 rounded-xl h-11 bg-muted/40 border-0" />
          </Q>
          <Q label="Check again">
            <DateChips prefix="followup" inputTestid="critical-followup-input" value={form.follow_up_date} onChange={set("follow_up_date")} presets={["1", "3", "weekend", "monday", "7", "14"]} none="No follow-up" />
          </Q>
          <Q label="Who's on it?" hint="They get a critical task due on the follow-up date.">
            <ChipGroup prefix="owner" options={ownerOptions} value={form.assignee_id} onChange={set("assignee_id")} allowClear={false} />
            {others.length > 0 && (
              <div className="mt-3 max-w-xs">
                <UserSelect value={other ? other.id : null} onChange={(v) => set("assignee_id")(v || "")} users={others} testid="critical-assignee-select" placeholder="Someone else on the team..." />
              </div>
            )}
          </Q>
          <label htmlFor="quarantine" className="mt-6 flex items-center justify-between gap-4 rounded-2xl bg-muted/50 p-4 cursor-pointer">
            <span>
              <span className="block text-sm font-medium">Quarantine this plant</span>
              <span className="block text-xs text-muted-foreground">Sets its status to Quarantine so others keep it apart.</span>
            </span>
            <Switch id="quarantine" data-testid="critical-quarantine-switch" checked={form.quarantine} onCheckedChange={(v) => setForm((f) => ({ ...f, quarantine: !!v }))} />
          </label>
        </>
      )}

      {cur === "review" && <Review form={form} plant={plant} users={users} me={user} goTo={goTo} />}
    </WizardShell>
  );
}
